import { unwrap, withSignal } from "@nexxt/api-client";
import { allPages, filterQuery } from "@nexxt/app-kit";
import type { ListCustomerOrdersInput } from "@nexxt/contracts/sales";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the customer's orders (pattern.md §10, ADR-219): one factory, the same keys for loaders and
// screens. The lines of an order keep their key under the order's.

/** The filters of the list (`listCustomerOrdersInput`), without the paging the queries add. */
export type OrderFilters = Pick<ListCustomerOrdersInput, "open" | "q">;

const PAGE_SIZE = 20;
/** The orders in progress the home shows: the latest ones, then the link to all of them. */
const HOME_SIZE = 5;

const orders = api.sales.orders;
const order = orders[":key"];

const keys = {
  all: ["orders"] as const,
  lists: ["orders", "list"] as const,
  of: (salesOrderId: string) => ["orders", "of", salesOrderId] as const,
};

export const orderQueries = {
  ...keys,

  list: (filters: OrderFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          orders.$get({ query: filterQuery({ ...filters, page: pageParam, pageSize: PAGE_SIZE }) }, withSignal(signal)),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** The latest orders in progress, for the home. */
  inProgress: () =>
    queryOptions({
      queryKey: [...keys.lists, "home"],
      queryFn: ({ signal }) =>
        unwrap(orders.$get({ query: filterQuery({ open: true, pageSize: HOME_SIZE }) }, withSignal(signal))),
    }),

  /**
   * The orders whose latest confirmation waits for the customer's answer (ADR-208, ADR-221), first on the home, a few
   * at a time.
   */
  toAnswer: () =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, "to-answer"],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          orders.$get(
            {
              query: { ...filterQuery({ open: true, page: pageParam, pageSize: HOME_SIZE }), confirmation: "awaiting" },
            },
            withSignal(signal),
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** By id or by number, with its shipments. */
  detail: (key: string) =>
    queryOptions({
      queryKey: [...keys.all, "detail", key],
      queryFn: ({ signal }) => unwrap(order.$get({ param: { key } }, withSignal(signal))),
    }),

  /** Every line of an order, for the claim form to offer them (ADR-228): an order of a shop has a few hundred at most. */
  allLines: (salesOrderId: string) =>
    queryOptions({
      queryKey: [...keys.of(salesOrderId), "lines", "all"],
      queryFn: ({ signal }) =>
        allPages((cursor) =>
          unwrap(
            orders[":salesOrderId"].lines.$get(
              { param: { salesOrderId }, query: filterQuery({ cursor, limit: 500 }) },
              withSignal(signal),
            ),
          ),
        ),
    }),

  lines: (salesOrderId: string) =>
    infiniteQueryOptions({
      queryKey: [...keys.of(salesOrderId), "lines"],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          orders[":salesOrderId"].lines.$get(
            { param: { salesOrderId }, query: filterQuery({ cursor: pageParam }) },
            withSignal(signal),
          ),
        ),
      initialPageParam: "",
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    }),
};
